import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Ic from "../icons.jsx";
import { tmApi, type TmAttachmentView } from "../../api/tm";
import { useToast } from "../Toast";

const ALLOWED = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/zip",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain", "text/csv",
];
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_FILES = 10;

type Props = {
  taskId: string;
  attachments: TmAttachmentView[];
  onUploaded: () => Promise<void>;
};

/**
 * Post-create attachment manager for a task. Picks file → presign → PUT MinIO →
 * confirm. Bytes never touch the BE.
 *
 * Image attachments get an inline thumbnail, fetched via the same presigned-GET
 * flow as the download button (so MinIO permissions are enforced consistently).
 */
export function TmAttachmentList({ taskId, attachments, onUploaded }: Props) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  // Lazy-load thumbnail URLs for image attachments. Each image gets a presigned
  // GET we render in <img>. We cache by attachment id and only request once
  // per id even if attachments[] re-references on parent rerender.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      for (const a of attachments) {
        if (!a.contentType.startsWith("image/")) continue;
        if (thumbs[a.id]) continue;
        try {
          const { url } = await tmApi.attachmentDownloadUrl(taskId, a.id);
          if (cancelled) return;
          setThumbs((prev) => ({ ...prev, [a.id]: url }));
        } catch {
          // skip — fallback icon will render
        }
      }
    }
    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, taskId]);

  function fileIcon(type: string) {
    if (type === "application/pdf") return <Ic.pdf />;
    if (type.startsWith("image/")) return <Ic.image />;
    if (type === "application/zip") return <Ic.zip />;
    return <Ic.copy />;
  }

  async function handleFile(f: File) {
    if (!canWrite) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error(t("tm.attachments.errType", "File type not allowed"));
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(t("tm.attachments.errSize", "Max 25 MB"));
      return;
    }
    if (attachments.length >= MAX_FILES) {
      toast.error(t("tm.attachments.errMax", { max: MAX_FILES, defaultValue: `Max ${MAX_FILES} attachments` }));
      return;
    }
    setBusy(true);
    try {
      const presigned = await tmApi.presignAttachment(taskId, {
        fileName: f.name, contentType: f.type, sizeBytes: f.size,
      });
      const res = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": f.type },
        body: f,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      await tmApi.confirmAttachment(taskId, {
        objectKey: presigned.objectKey,
        fileName: f.name, contentType: f.type, sizeBytes: f.size,
      });
      toast.success(t("tm.attachments.uploaded", "Attachment uploaded."));
      await onUploaded();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function download(a: TmAttachmentView) {
    try {
      const { url } = await tmApi.attachmentDownloadUrl(taskId, a.id);
      window.open(url, "_blank");
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  const atLimit = attachments.length >= MAX_FILES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {attachments.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {attachments.map((a) => {
            const isImage = a.contentType.startsWith("image/");
            const thumbUrl = thumbs[a.id];
            return (
              <li key={a.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px",
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 13,
              }}>
                {isImage && thumbUrl ? (
                  <a
                    href={thumbUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ flex: "none", display: "block" }}
                    aria-label={a.fileName}
                  >
                    <img
                      src={thumbUrl}
                      alt=""
                      style={{
                        width: 44, height: 44, borderRadius: 6,
                        objectFit: "cover",
                        border: "1px solid var(--border)",
                        background: "var(--surface)",
                        display: "block",
                      }}
                    />
                  </a>
                ) : (
                  <span style={{
                    display: "grid", placeItems: "center",
                    width: 44, height: 44, borderRadius: 6,
                    background: "var(--surface)",
                    color: "var(--ink-3)", flex: "none",
                    border: "1px solid var(--border)",
                  }}>
                    {fileIcon(a.contentType)}
                  </span>
                )}
                <span style={{ flex: 1, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.fileName}
                </span>
                <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  {(a.sizeBytes / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => download(a)}
                  className="btn sm"
                  style={{ padding: "4px 8px" }}
                >
                  {t("tm.attachments.download", "Download")}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED.join(",")}
          disabled={!canWrite || busy || atLimit}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn"
          onClick={() => inputRef.current?.click()}
          disabled={!canWrite || busy || atLimit}
        >
          <Ic.upload />
          {atLimit
            ? t("tm.attachments.atLimit", "Limit reached")
            : busy
              ? t("tm.attachments.uploading", "Uploading…")
              : t("tm.attachments.add", "Add file")}
        </button>
      </div>
    </div>
  );
}
